import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { Builder } from "api-types/modelTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import Showroom from "lib/components/builders/communities/plans/showroom/Showroom";
import LoadingPage from "lib/components/layout/LoadingPage";
import { useFetchImmutable } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import getPageTitle from "lib/utils/getPageTitle";
import type { ShowroomTemplatePlanData } from "lib/utils/showroom/showroomData";
import { createTemplatePlanData } from "lib/utils/showroom/showroomData";
import { Helmet as Head } from "react-helmet-async";
import withAuthentication from "../../../../../components/withAuthentication";

function MasterPlanShowroomPage() {
  const { isReady, builderId, masterPlanId } = useAppRouterParams();

  if (!isReady || !builderId || !masterPlanId) {
    return <LoadingPage />;
  }

  return <ReadyMasterPlanShowroomPage builderId={builderId} masterPlanId={masterPlanId} />;
}

function ReadyMasterPlanShowroomPage({ masterPlanId, builderId }: { masterPlanId: string; builderId: string }) {
  const { data: builder } = useFetchImmutable<Builder>(`${WEB_API_BASE_PATH}/builders/${builderId}`, [builderId]);
  const { data: masterPlan } = useFetchImmutable<ClientMasterPlanHydrated>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}/hydrated`,
    [builderId, masterPlanId],
  );

  // Populate fake data to pass to Showroom, minus the available builder and builder plan info
  const planData: ShowroomTemplatePlanData | null =
    builder && masterPlan ? createTemplatePlanData(builder, masterPlan) : null;

  if (!planData || !builder || !masterPlan) {
    return <LoadingPage />;
  }

  return (
    <>
      <Head>
        <title>{getPageTitle(`Configure ${masterPlan.name}`)}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <Showroom
        builder={planData.builder}
        location={planData.community}
        lot={planData.lot}
        masterPlan={planData.masterPlan}
        locationPlan={planData.communityPlan}
        homePlan={planData.homePlan}
        lotDiscounts={planData.lotDiscounts}
        homePlanDiscounts={planData.homePlanDiscounts}
        elevationOverrides={masterPlan.elevationOverrides}
        previewMode
      />
    </>
  );
}

export const Route = createFileRoute("/builders/$builder_id/master-plans/$master_plan_id/showroom")({
  beforeLoad: ({ context, params }) => {
    if (!context.enabledFeatures.showroomUnification) {
      return;
    }
    throw redirect({
      to: "/showroom/preview",
      search: { builderId: params.builder_id, planType: "master_plans", planId: params.master_plan_id },
      replace: true,
    });
  },
  component: withAuthentication(MasterPlanShowroomPage, {
    permissions: ["VIEW_HOME_PLANS"],
    products: ["showroom"],
  }),
});
